import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Close, CloseOutlined, Compress, Expand, FilterListOutlined, Search, VerticalAlignCenter } from '@mui/icons-material'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  debounce,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  ToggleButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { ArrowDropDownIcon } from '@mui/x-date-pickers/icons'

import UserModal from '../../../components/user-modal'
import { useAuthentication } from '../../../providers/auth-provider'
import useSharedStyles from '../../../utils/shared.styles'
import AccountsContext from '../context'
import useStyles from '../index.styles'

import { GroupByKey } from './devices-tab-panel/types'

export const DEACTIVATED_URL_PARAM = 'hideDeactivated'

const groupByMatrix: { [x: string]: string } = {
  alertSeverity: 'Alert Severity',
  deviceHealth: 'Device Health',
  status: 'Status',
  gateway: 'Gateway',
  deviceType: 'Device Type',
  solution: 'Solution',
  location: 'Location',
  none: 'None',
}

const SearchComponent = () => {
  const classes = useStyles
  const sharedClasses = useSharedStyles
  const theme = useTheme()

  const [showAddNewUserModal, setShowAddNewUserModal] = useState<boolean>(false)
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<null | HTMLElement>(null)
  const [deviceActionMenuAnchor, setDeviceActionMenuAnchor] = useState<null | HTMLElement>(null)
  const deviceMenuRef = useRef<HTMLDivElement>(null)

  const {
    folder,
    setPeopleTabRefreshCounter,
    isAccountView,
    setSearchText,
    tabIndex,
    deviceView,
    setDeviceView,
    deviceAction,
    setDeviceAction,
    pausedDeviceCount,
    firmwareVersions,
    deviceGroupBy,
    setDeviceGroupBy,
    deviceDisplayFirmware,
    setDeviceDisplayFirmware,
    deviceHideDeactivated,
    setDeviceHideDeactivated,
    setPauseModalStatus,
    setUnpauseModalStatus,
    setReturnModalStatus,
    setMoveDeviceModalStatus,
    setDeactivateModalStatus,
    selectedDeviceIds,
    setDenseViewForTableMultiselect,
    denseViewForTableMultiselect,
  } = useContext(AccountsContext)

  const { hasInternalUserAccess } = useAuthentication()

  const [searchValue, setSearchValue] = useState<string>('')

  const [deviceGroupByPending, setDeviceGroupByPending] = useState<
    'alertSeverity' | 'deviceHealth' | 'status' | 'gateway' | 'deviceType' | 'solution' | 'location' | 'none'
  >(deviceGroupBy)
  const [deviceDisplayFirmwarePending, setDeviceDisplayFirmwarePending] = useState<string[]>(deviceDisplayFirmware)
  const [deviceHideDeactivatedPending, setDeviceHideDeactivatedPending] = useState<boolean>(deviceHideDeactivated)

  const isFolderTab = (tabIndex === 'accounts' || tabIndex === 'programs' || tabIndex === 'participants') && !isAccountView
  const isContactsTab = tabIndex === 'people' || tabIndex === 'contacts'
  const isDevicesTab = tabIndex === 'devices'

  //  TODO commenting out for now since ticket suggests we might want to add Hide Deactivated functionality back;
  //  we can remove this commented code if we are not adding this functionality back in a reasonable time
  // const [searchParams, setSearchParams] = useSearchParams()
  // const hideDeactivated = searchParams.get(DEACTIVATED_URL_PARAM) === 'true'

  const noDevicesSelected = selectedDeviceIds.ids.size === 0

  // Clear search when the folder id changes
  useEffect(() => {
    setSearchValue('')
    setSearchText('')
  }, [folder?.id, setSearchText])

  // clear search and filters if user changes tabs
  useEffect(() => {
    setSearchValue('')
    setSearchText('')
    setDeviceGroupByPending('alertSeverity')
    setDeviceDisplayFirmwarePending([])
    setDeviceHideDeactivatedPending(false)
    setDenseViewForTableMultiselect(false)
  }, [setDenseViewForTableMultiselect, setSearchText, tabIndex])

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setSearchValue(event.target.value)
    },
    [setSearchValue]
  )

  const onSearch = useCallback(
    (newValue: string): void => {
      setSearchText(newValue)
      setDeviceDisplayFirmware([])
      setDeviceHideDeactivated(false)
    },
    [setDeviceDisplayFirmware, setDeviceHideDeactivated, setSearchText]
  )

  const getSearchResults = useMemo(() => debounce((value) => onSearch(value), 500), [onSearch])

  useEffect(() => {
    getSearchResults(searchValue)
  }, [getSearchResults, searchValue])

  const selectDeviceAction = (action: 'pause' | 'unpause' | 'rma' | 'move' | 'deactivate' | undefined) => {
    setDeviceAction(action)
    setDeviceActionMenuAnchor(null)
  }

  const doBulkAction = () => {
    if (deviceAction === 'pause') setPauseModalStatus({ open: true, devices: [] })
    if (deviceAction === 'unpause') setUnpauseModalStatus({ open: true, devices: [] })
    if (deviceAction === 'rma') setReturnModalStatus({ open: true, devices: [] })
    if (deviceAction === 'deactivate') setDeactivateModalStatus({ open: true, devices: [] })
    if (deviceAction === 'move') setMoveDeviceModalStatus((prev) => ({ ...prev, open: true }))
  }

  const getActionButtonVariant = () => {
    if (!deviceAction) {
      return 'outlined'
    }

    if (noDevicesSelected) {
      return 'contained'
    }

    return 'contained'
  }

  const deviceFiltersAreDefault = deviceGroupBy === 'alertSeverity' && deviceDisplayFirmware.length === 0 && !deviceHideDeactivated
  const showWideSearchBar = !isAccountView

  return (
    <Box sx={classes.searchContainer}>
      <TextField
        value={searchValue}
        onChange={onChange}
        placeholder={showWideSearchBar ? 'Search current page' : 'Search'}
        variant="outlined"
        size="small"
        sx={classes.searchTextField}
        slotProps={{
          input: {
            startAdornment: <Search fontSize="small" sx={classes.searchIcon} />,
            endAdornment: searchValue ? (
              <IconButton onClick={() => setSearchValue('')} aria-label="Clear Search">
                <Close fontSize="small" sx={sharedClasses.secondaryText} />
              </IconButton>
            ) : null,
            sx: showWideSearchBar ? classes.searchTextFieldInputWide : classes.searchTextFieldInput,
          },
        }}
      />
      {isDevicesTab && (
        <>
          {denseViewForTableMultiselect === false && (
            <ButtonGroup variant="outlined" aria-label="Change view">
              <Tooltip title="Default View">
                <Button
                  onClick={() => {
                    setDeviceView('default')
                  }}
                  sx={{ ...(deviceView === 'default' ? classes.deviceViewActiveButton(theme) : {}) }}
                >
                  <Expand
                    color={deviceView === 'default' ? 'primary' : 'action'}
                    aria-label={deviceView === 'default' ? 'Default view (selected)' : 'Default view'}
                  />
                </Button>
              </Tooltip>

              <Tooltip title="Compact View">
                <Button onClick={() => setDeviceView('compact')} sx={{ ...(deviceView === 'compact' ? classes.deviceViewActiveButton(theme) : {}) }}>
                  <Compress
                    color={deviceView === 'compact' ? 'primary' : 'action'}
                    aria-label={deviceView === 'compact' ? 'Compact view (selected)' : 'Compact view'}
                  />
                </Button>
              </Tooltip>

              <Tooltip title="Micro View">
                <Button onClick={() => setDeviceView('micro')} sx={{ ...(deviceView === 'micro' ? classes.deviceViewActiveButton(theme) : {}) }}>
                  <VerticalAlignCenter
                    color={deviceView === 'micro' ? 'primary' : 'action'}
                    aria-label={deviceView === 'micro' ? 'Micro view (selected)' : 'Micro view'}
                  />
                </Button>
              </Tooltip>
            </ButtonGroup>
          )}

          {hasInternalUserAccess && (
            <Tooltip title={noDevicesSelected ? 'Please select at least one device' : undefined} placement="bottom">
              <ButtonGroup variant={getActionButtonVariant()} aria-label="Device actions" ref={deviceMenuRef}>
                <Button
                  onClick={() => (!deviceAction ? setDeviceActionMenuAnchor(deviceMenuRef.current) : doBulkAction())}
                  disabled={noDevicesSelected && Boolean(deviceAction)}
                >
                  {!deviceAction ? 'Actions' : deviceAction}
                </Button>

                <Button
                  size="small"
                  aria-controls={deviceActionMenuAnchor ? 'split-button-menu' : undefined}
                  aria-expanded={deviceActionMenuAnchor ? 'true' : undefined}
                  aria-label="Select device action"
                  aria-haspopup="menu"
                  onClick={() => setDeviceActionMenuAnchor(deviceMenuRef.current)}
                  variant={getActionButtonVariant()}
                >
                  <ArrowDropDownIcon />
                </Button>
              </ButtonGroup>
            </Tooltip>
          )}

          <Menu
            anchorEl={deviceActionMenuAnchor}
            open={Boolean(deviceActionMenuAnchor)}
            onClose={() => setDeviceActionMenuAnchor(null)}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem
              onClick={() => {
                selectDeviceAction('pause')
                setDenseViewForTableMultiselect(false)
              }}
            >
              Pause
            </MenuItem>

            <MenuItem
              onClick={() => {
                selectDeviceAction('unpause')
                setDenseViewForTableMultiselect(false)
              }}
              disabled={pausedDeviceCount === 0}
            >
              Unpause
            </MenuItem>
            <MenuItem
              onClick={() => {
                selectDeviceAction('rma')
                setDenseViewForTableMultiselect(true)
              }}
            >
              RMA
            </MenuItem>
            <MenuItem
              onClick={() => {
                selectDeviceAction('move')
                setDenseViewForTableMultiselect(true)
              }}
            >
              Move
            </MenuItem>
            <MenuItem
              onClick={() => {
                selectDeviceAction('deactivate')
                setDenseViewForTableMultiselect(true)
              }}
            >
              Deactivate
            </MenuItem>
          </Menu>

          <ToggleButton
            selected={Boolean(filterMenuAnchor)}
            value="check"
            size="small"
            onClick={(e) => {
              // If filters and groups are not default, reset them
              if (!deviceFiltersAreDefault) {
                // Reset filters and groups
                setDeviceGroupBy('alertSeverity')
                setDeviceGroupByPending('alertSeverity')
                setDeviceDisplayFirmware([])
                setDeviceDisplayFirmwarePending([])
                setDeviceHideDeactivated(false)
                setDeviceHideDeactivatedPending(false)
              } else {
                // Otherwise, open the filter dropdown
                setFilterMenuAnchor(e.currentTarget)
              }
            }}
            aria-label={filterMenuAnchor || deviceFiltersAreDefault ? 'Filter devices' : 'Reset filters'}
            sx={classes.filterButton}
          >
            {filterMenuAnchor || deviceFiltersAreDefault ? <FilterListOutlined /> : <CloseOutlined color="primary" />}
          </ToggleButton>
        </>
      )}
      {isContactsTab && (
        <Button
          variant="outlined"
          startIcon={<AddOutlinedIcon />}
          sx={classes.newUserButton}
          onClick={() => setShowAddNewUserModal(true)}
          aria-label="Create New User"
        >
          New
        </Button>
      )}
      {/* TODO commenting out for now since ticket suggests we might want to add this back;
       * we can remove this commented code if we are not adding this functionality back in a reasonable time
       */}
      {/* {isFolderTab && (
        <ToggleButton
          selected={Boolean(filterMenuAnchor)}
          value="check"
          size="small"
          onClick={(e) => {
            if (hideDeactivated) {
              searchParams.delete(DEACTIVATED_URL_PARAM)
              setSearchParams(searchParams)
            } else {
              setFilterMenuAnchor(e.currentTarget)
            }
          }}
          aria-label="Filter accounts"
          className={classes.filterButton}
        >
          {filterMenuAnchor || !hideDeactivated ? <FilterListOutlined /> : <CloseOutlined color="primary" />}
        </ToggleButton>
      )} */}
      {!isFolderTab && (
        <Menu
          anchorEl={filterMenuAnchor}
          open={Boolean(filterMenuAnchor)}
          onClose={() => setFilterMenuAnchor(null)}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {/* TODO commenting out for now since ticket suggests we might want to add this back;
           * we can remove this commented code if we are not adding this functionality back in a reasonable time
           */}
          {/* {isFolderTab && (
         <FormControlLabel
           control={
             <Switch
               value={hideDeactivated}
               onClick={() => {
                 if (hideDeactivated) {
                   searchParams.delete(DEACTIVATED_URL_PARAM)
                 } else {
                   searchParams.set(DEACTIVATED_URL_PARAM, 'true')
                 }
                 setSearchParams(searchParams)
               }}
             />
           }
           label="Hide deactivated"
           className={classes.switchContainer}
         />
       )} */}
          {isDevicesTab && (
            <div>
              <Grid container={true}>
                <Grid sx={classes.deviceFilterContainer}>
                  <Typography sx={sharedClasses.secondaryText}>Group by</Typography>
                  <RadioGroup name="groupBy" defaultValue="alertSeverity">
                    <Grid container={true} sx={{ flexDirection: 'column' }} size={12}>
                      {['alertSeverity', 'deviceHealth', 'status', 'gateway', 'deviceType', 'solution', 'location', 'none'].map((value) => (
                        <FormControlLabel
                          key={value}
                          control={<Radio checked={deviceGroupByPending === value} />}
                          onChange={() => setDeviceGroupByPending(value as GroupByKey)}
                          value={value}
                          label={groupByMatrix[value]}
                        />
                      ))}
                    </Grid>
                  </RadioGroup>
                </Grid>
                <Grid sx={classes.deviceFilterContainer}>
                  <Typography sx={sharedClasses.secondaryText}>Display Only</Typography>
                  <Grid container={true} sx={{ flexDirection: 'column' }} size={12}>
                    {firmwareVersions.map((version) => (
                      <FormControlLabel
                        key={version}
                        control={<Checkbox checked={deviceDisplayFirmwarePending.includes(version)} />}
                        onChange={(_e, checked) =>
                          checked
                            ? setDeviceDisplayFirmwarePending((prev) => [...prev, version])
                            : setDeviceDisplayFirmwarePending((prev) => prev.filter((v) => v !== version))
                        }
                        label={`v${version}`}
                      />
                    ))}
                  </Grid>
                  <Typography sx={sharedClasses.secondaryText}>Hide</Typography>
                  <Grid container={true} sx={{ flexDirection: 'column' }} size={12}>
                    <FormControlLabel
                      control={<Switch checked={deviceHideDeactivatedPending} />}
                      onChange={(_e, checked) => setDeviceHideDeactivatedPending(checked)}
                      label="Deactivated Devices"
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Divider />
              <Grid
                container={true}
                sx={[
                  {
                    gap: '4px',
                  },
                  classes.deviceFilterButtonContainer,
                ]}
              >
                <Button
                  variant="contained"
                  disabled={
                    deviceGroupBy === deviceGroupByPending &&
                    deviceDisplayFirmwarePending.length === deviceDisplayFirmware.length &&
                    deviceDisplayFirmwarePending.every((v) => deviceDisplayFirmware.includes(v)) &&
                    deviceHideDeactivated === deviceHideDeactivatedPending
                  }
                  onClick={() => {
                    setDeviceGroupBy(deviceGroupByPending)
                    setDeviceDisplayFirmware(deviceDisplayFirmwarePending)
                    setDeviceHideDeactivated(deviceHideDeactivatedPending)
                    setFilterMenuAnchor(null)
                  }}
                >
                  Apply
                </Button>
                <Button
                  disabled={deviceGroupByPending === 'alertSeverity' && deviceDisplayFirmwarePending.length === 0 && deviceHideDeactivatedPending === false}
                  onClick={() => {
                    setDeviceGroupBy('alertSeverity')
                    setDeviceGroupByPending('alertSeverity')
                    setDeviceDisplayFirmware([])
                    setDeviceDisplayFirmwarePending([])
                    setDeviceHideDeactivated(false)
                    setDeviceHideDeactivatedPending(false)
                  }}
                >
                  Reset
                </Button>
              </Grid>
            </div>
          )}
        </Menu>
      )}
      <UserModal
        open={showAddNewUserModal}
        setOpen={setShowAddNewUserModal}
        mode="create"
        setPeopleTabRefreshCounter={setPeopleTabRefreshCounter}
        user={undefined}
      />
    </Box>
  )
}

export default SearchComponent
